import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import type { MultiValue } from 'react-select';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { GetEmployeeDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';

import { Selector, Option } from './Selector';

export interface EmployeeSelectorProps {
  fetchForOrders?: boolean;
  fetchAuthorized?: boolean;
  dateForAuthorized?: Date;
  projectIdForAuthorized?: string;
  readOnly?: boolean;
  isMulti?: boolean;
  value?: string | string[];
  className?: string;
  onlyCurrentCompany?: boolean;
  onlyFromCompany?: string;
  withTechnical?: boolean;
  withOneToolId?: boolean;
  disableAutoFilter?: boolean;
  setOptions?: (employees: GetEmployeeDto[]) => void;
  onChange?: (option: Option | MultiValue<Option> | null | undefined) => void;
}

export default (props: EmployeeSelectorProps) => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const [employees, setEmployees] = useState<GetEmployeeDto[]>([]);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [currentCompanyId, setCurrentCompanyId] = useState<string>();
  const [date, setDate] = useState<Date | undefined>(props.dateForAuthorized);
  const [projectId, setProjectId] = useState<string | undefined>(
    props.projectIdForAuthorized,
  );

  useEffect(() => {
    let mounted = true;
    if (
      employeesLoaded &&
      (props.onlyFromCompany !== currentCompanyId ||
        date !== props.dateForAuthorized ||
        projectId !== props.projectIdForAuthorized)
    ) {
      setEmployeesLoaded(false);
      setEmployees([]);
      setDate(props.dateForAuthorized);
      setProjectId(props.projectIdForAuthorized);
    }
    if (!employees.length && !employeesLoaded) {
      let companyId = undefined;
      if (props.onlyCurrentCompany) {
        companyId = pok.getCompanyId();
      }
      if (props.onlyFromCompany) {
        companyId = props.onlyFromCompany;
      }
      setCurrentCompanyId(companyId);
      let fetchEmployees;

      if (props.fetchForOrders) {
        fetchEmployees = pok.employees.getAllForOrders();
      } else if (props.fetchAuthorized) {
        fetchEmployees = pok.employees.getAllForAuthorized(
          props.dateForAuthorized,
          props.projectIdForAuthorized,
        );
      } else {
        fetchEmployees = pok.employees.getByCompanyId(
          companyId,
          props.withTechnical,
        );
      }

      fetchEmployees
        ?.then(data => {
          if (mounted) {
            setEmployees(data);
            props.setOptions?.(data);
            setEmployeesLoaded(true);
          }
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    }

    return () => {
      mounted = false;
    };
  }, [
    pok.employees,
    pok.getCompanyId,
    pok,
    employees,
    employeesLoaded,
    currentCompanyId,
    notifications,
    props,
    date,
    projectId,
  ]);

  useEffect(() => {
    // Wyłącz automatyczne filtrowanie jeśli disableAutoFilter jest true
    if (props.disableAutoFilter) {
      return;
    }
    if (employees && props.onChange) {
      if (props.isMulti) {
        const newEmployees = employees?.filter(({ id }) =>
          props.value?.includes(id),
        );
        if ((props.value?.length || 0) !== (newEmployees?.length || 0)) {
          props.onChange(
            newEmployees.map(v => ({ value: v.id, label: v.name }) as Option),
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, props.value, props.isMulti, props.disableAutoFilter]);

  const provider = async (text: string) => {
    const values = employees.filter(v =>
      v.name?.toLocaleLowerCase().includes(text.toLocaleLowerCase()),
    );

    return values.map(v => {
      return {
        badge: '',
        label: props.withOneToolId
          ? `${v.name} (${v.supervisor} - OneTool: ${v.oneToolId})`
          : `${v.name} (${v.supervisor})`,
        value: v.id,
      };
    });
  };

  if (!employees.length && !employeesLoaded) {
    return <Skeleton />;
  }

  return (
    <Selector
      uniqueKey={
        'empl-' + JSON.stringify(employees) + JSON.stringify(props.value)
      }
      className={props.className}
      isMulti={props.isMulti}
      readOnly={props.readOnly}
      creatable={false}
      provider={provider}
      value={props.value}
      deletedValueName={id =>
        pok.employees.getOptionById(id, props.withOneToolId)
      }
      onChange={option => props.onChange && props.onChange(option as Option)}
    />
  );
};
